import { __, sprintf } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import { Button, Spinner, Notice, CheckboxControl } from '@wordpress/components';
import { ImageModal } from './';

const UnusedMediaTab = ({ refreshStats }) => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState({});
    const [notice, setNotice] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);

    // Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [bulkAction, setBulkAction] = useState('');
    const [availableDates, setAvailableDates] = useState([]);

    // Pagination state
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const limit = 20;

    useEffect(() => {
        fetchUnusedMedia();
        setSelectedItems([]); // Reset selection on page change
    }, [page, dateFilter]); // Re-fetch on page or date change if we want instant date filtering, or stay manual. User said "like pages/posts" which often requires clicking Filter button.

    // On initial load, fetch available dates
    useEffect(() => {
        fetchAvailableDates();
    }, []);

    const fetchAvailableDates = async () => {
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_get_unused_dates',
                    nonce: window.mtrimAdminData.nonce
                })
            });
            const data = await response.json();
            if (data.success) {
                setAvailableDates(data.data);
            }
        } catch (error) {
            console.error('Error fetching dates:', error);
        }
    };

    const fetchUnusedMedia = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'mtrim_get_unused_media',
                    nonce: window.mtrimAdminData.nonce,
                    page: page,
                    limit: limit,
                    search: searchQuery,
                    mtrim_date: dateFilter
                })
            });

            const data = await response.json();
            if (data.success) {
                setItems(data.data.items);
                setTotalPages(data.data.pages);
                setTotalItems(data.data.total);
            }
        } catch (error) {
            console.error('Error fetching unused media:', error);
        }
        setIsLoading(false);
    };

    const handleDelete = async (attachmentId) => {
        if (!confirm(__('Are you sure you want to move this item to trash?', 'media-trim'))) {
            return;
        }

        setActionLoading({ ...actionLoading, [attachmentId]: true });
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_delete_media',
                    nonce: window.mtrimAdminData.nonce,
                    attachment_id: attachmentId,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setNotice({ type: 'success', message: data.data.message });
                refreshStats();
                
                // If we deleted the last item on a page (but not the first page), go back
                if (items.length === 1 && page > 1) {
                    setPage(page - 1);
                } else {
                    fetchUnusedMedia();
                }
            } else {
                setNotice({ type: 'error', message: data.data.message });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to move to trash', 'media-trim') });
        }
        setActionLoading({ ...actionLoading, [attachmentId]: false });
    };

    const handleDeleteAll = async () => {
        if (!confirm(__('Are you sure you want to move ALL unused media to trash?', 'media-trim'))) {
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_delete_all_unused',
                    nonce: window.mtrimAdminData.nonce,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setItems([]);
                setPage(1);
                setNotice({ type: 'success', message: data.data.message });
                refreshStats();
            } else {
                setNotice({ type: 'error', message: data.data.message });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to delete all unused items', 'media-trim') });
        }
        setIsLoading(false);
    };

    const handleBulkDelete = async () => {
        if (selectedItems.length === 0) return;
        if (!confirm(sprintf(__('Are you sure you want to move %d selected items to trash?', 'media-trim'), selectedItems.length))) {
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_bulk_delete_media',
                    nonce: window.mtrimAdminData.nonce,
                    attachment_ids: JSON.stringify(selectedItems),
                }),
            });
            const data = await response.json();
            if (data.success) {
                setNotice({ type: 'success', message: data.data.message });
                setSelectedItems([]);
                refreshStats();
                fetchUnusedMedia();
            } else {
                setNotice({ type: 'error', message: data.data.message });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to perform bulk deletion', 'media-trim') });
        }
        setIsLoading(false);
    };

    const handleBulkActionApply = () => {
        if (!bulkAction) return;
        if (selectedItems.length === 0) {
            alert(__('Please select some items first.', 'media-trim'));
            return;
        }

        if (bulkAction === 'trash') {
            handleBulkDelete();
        }
        setBulkAction('');
    };

    const handleFilterApply = () => {
        setPage(1);
        fetchUnusedMedia();
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setDateFilter('');
        setPage(1);
        // fetch will be triggered by states if we had it in deps, or manually
        setTimeout(() => fetchUnusedMedia(), 0);
    };

    const toggleSelectAll = () => {
        if (selectedItems.length === items.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(items.map(item => item.attachment_id));
        }
    };

    const toggleSelectItem = (id) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter(i => i !== id));
        } else {
            setSelectedItems([...selectedItems, id]);
        }
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (page <= 4) {
                pages.push(1, 2, 3, 4, 5, '...', totalPages);
            } else if (page >= totalPages - 3) {
                pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
            }
        }

        return (
            <div className="mtrim-pagination-wrapper">
                <div className="mtrim-pagination">
                    <button 
                        className="mtrim-pagination-btn"
                        disabled={page === 1} 
                        onClick={() => setPage(page - 1)}
                        title={__('Previous Page', 'media-trim')}
                    >
                        <span className="dashicons dashicons-arrow-left-alt2"></span>
                    </button>
                    
                    {pages.map((p, index) => (
                        p === '...' ? (
                            <span key={`ellipsis-${index}`} className="mtrim-pagination-btn is-ellipsis">...</span>
                        ) : (
                            <button 
                                key={p}
                                className={`mtrim-pagination-btn ${page === p ? 'is-active' : ''}`}
                                onClick={() => setPage(p)}
                            >
                                {p}
                            </button>
                        )
                    ))}

                    <button 
                        className="mtrim-pagination-btn"
                        disabled={page === totalPages} 
                        onClick={() => setPage(page + 1)}
                        title={__('Next Page', 'media-trim')}
                    >
                        <span className="dashicons dashicons-arrow-right-alt2"></span>
                    </button>
                </div>
                <div className="mtrim-page-info">
                    {sprintf(__('Page %d of %d', 'media-trim'), page, totalPages)}
                    <span className="mtrim-separator">|</span>
                    {sprintf(__('Total %d items', 'media-trim'), totalItems)}
                </div>
            </div>
        );
    };

    return (
        <div className="mtrim-results-tab">
            <div className="mtrim-results-header">
                <h2>{__('Unused Media', 'media-trim')} <span>({totalItems})</span></h2>
                <div className="mtrim-results-actions">
                    <Button 
                        isSecondary
                        onClick={fetchUnusedMedia} 
                        disabled={isLoading}
                        icon="update"
                    />
                    <Button 
                        isDestructive 
                        onClick={handleDeleteAll}
                        disabled={items.length === 0 || isLoading}
                    >
                        {__('Delete All Unused', 'media-trim')}
                    </Button>
                </div>
            </div>

            <div className="mtrim-filter-area">
                <div className="mtrim-filter-left">
                    <div className="mtrim-filter-group">
                        <select 
                            value={bulkAction} 
                            onChange={(e) => setBulkAction(e.target.value)}
                            className="mtrim-filter-select"
                        >
                            <option value="">{__('Bulk Actions', 'media-trim')}</option>
                            <option value="trash">{__('Move to Trash', 'media-trim')}</option>
                        </select>
                        <Button isSecondary onClick={handleBulkActionApply}>
                            {__('Apply', 'media-trim')}
                        </Button>
                    </div>

                    <div className="mtrim-filter-group">
                        <select 
                            value={dateFilter} 
                            onChange={(e) => setDateFilter(e.target.value)}
                            className="mtrim-filter-select"
                        >
                            <option value="">{__('All dates', 'media-trim')}</option>
                            {/* We should ideally populate this from backend */}
                            {availableDates.map(date => (
                                <option key={date.value} value={date.value}>{date.label}</option>
                            ))}
                        </select>
                        <Button isSecondary onClick={handleFilterApply}>
                            {__('Filter', 'media-trim')}
                        </Button>
                    </div>
                    
                    {(searchQuery || dateFilter) && (
                        <Button isLink onClick={handleClearFilters}>
                            {__('Clear', 'media-trim')}
                        </Button>
                    )}
                </div>
                <div className="mtrim-filter-right">
                    {selectedItems.length > 0 && (
                        <div className="mtrim-selection-info">
                            <span>{sprintf(__('%d selected', 'media-trim'), selectedItems.length)}</span>
                            <Button isLink onClick={() => setSelectedItems([])}>{__('Clear', 'media-trim')}</Button>
                        </div>
                    )}
                    <input 
                        type="search" 
                        placeholder={__('Search media...', 'media-trim')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleFilterApply()}
                    />
                    <Button isSecondary onClick={handleFilterApply}>
                        {__('Search', 'media-trim')}
                    </Button>
                </div>
            </div>

            {notice && (
                <Notice 
                    status={notice.type} 
                    isDismissible={true}
                    onRemove={() => setNotice(null)}
                    style={{ marginBottom: '20px' }}
                >
                    {notice.message}
                </Notice>
            )}


            {isLoading && items.length === 0 ? (
                <div className="mtrim-loading-state">
                    <Spinner />
                    <p>{__('Loading results...', 'media-trim')}</p>
                </div>
            ) : items.length === 0 ? (
                <div className="mtrim-empty-state">
                    <span className="dashicons dashicons-media-code"></span>
                    <p>{__('No unused media found. Run a scan to get started.', 'media-trim')}</p>
                </div>
            ) : (
                <div className="mtrim-table-container">
                    <table className="mtrim-results-table">
                        <thead>
                            <tr>
                                <th className="mtrim-column-checkbox">
                                    <CheckboxControl
                                        checked={selectedItems.length === items.length && items.length > 0}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th>{__('Preview', 'media-trim')}</th>
                                <th>{__('File Name', 'media-trim')}</th>
                                <th>{__('Size', 'media-trim')}</th>
                                <th>{__('Date', 'media-trim')}</th>
                                <th>{__('Actions', 'media-trim')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(item => (
                                <tr key={item.attachment_id} className={selectedItems.includes(item.attachment_id) ? 'is-selected' : ''}>
                                    <td className="mtrim-column-checkbox">
                                        <CheckboxControl
                                            checked={selectedItems.includes(item.attachment_id)}
                                            onChange={() => toggleSelectItem(item.attachment_id)}
                                        />
                                    </td>
                                    <td>
                                        <div 
                                            className="mtrim-media-preview" 
                                            onClick={() => setSelectedImage(item.url)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {item.url ? <img src={item.url} alt="" /> : <span className="dashicons dashicons-media-default"></span>}
                                        </div>
                                    </td>
                                    <td className="mtrim-column-filename">
                                        <strong>{item.filename}</strong>
                                        <code>{item.file_path}</code>
                                    </td>
                                    <td>{item.formatted_size}</td>
                                    <td>{item.date}</td>
                                    <td>
                                        <Button 
                                            isDestructive 
                                            icon="trash"
                                            onClick={() => handleDelete(item.attachment_id)}
                                            disabled={actionLoading[item.attachment_id]}
                                            title={__('Move to Trash', 'media-trim')}
                                        >
                                            {actionLoading[item.attachment_id] && <Spinner />}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {renderPagination()}
                </div>
            )}
        </div>
    );
};

export default UnusedMediaTab;
